import { useContext, useEffect } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import Table from 'react-bootstrap/Table';
import { inject, observer, PropTypes as MobXPropTypes } from 'mobx-react';
import EmptyTableContentPlug from 'components/EmptyTableContentPlug';
import Loader from 'components/Loader';
import i18nContext from 'components/i18n-context';
import { ROUTE_PATHS } from 'routes/constants';
import { formatDateTime } from 'services/utils';
import Button from 'uikit/Button/Button';
import IntegrationSettingSourceBadge from 'pages/Settings/components/IntegrationSettingSourceBadge';
import './IntegrationSettingsTab.scss';

const TABLE_HEADER = ['setting', 'description', 'source', 'version', 'lastChange', 'actions'];

const IntegrationSettingsTab = ({ integrationSettingsStore }) => {
  const i18n = useContext(i18nContext);
  const navigate = useNavigate();

  useEffect(() => {
    integrationSettingsStore.getIntegrationSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!integrationSettingsStore.isInitialized) {
    return <Loader className={'application-loader'} />;
  }

  const openSetting = ({ provider, settingKey }) =>
    navigate(generatePath(ROUTE_PATHS.SETTINGS_INTEGRATION_SETTING, { provider, settingKey }));

  return (
    <div className={'integration-settings-wrapper'}>
      <div className={'integration-settings-header'}>
        <h3 className={'integration-settings-title'}>{i18n.getMessage('settings.integrations.title')}</h3>
        <p className={'integration-settings-description'}>{i18n.getMessage('settings.integrations.description')}</p>
      </div>
      {integrationSettingsStore.settings.length === 0 ? (
        <EmptyTableContentPlug
          headerText={i18n.getMessage('settings.integrations.empty.header')}
          descriptionText={i18n.getMessage('settings.integrations.empty.description')}
        />
      ) : (
        <Table responsive>
          <thead>
            <tr>
              {TABLE_HEADER.map((head) => (
                <th key={head} className={'integration-settings-table-head'}>
                  {head !== 'actions' && i18n.getMessage(`settings.integrations.table.header.${head}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {integrationSettingsStore.settings.map((setting) => (
              <tr key={`${setting.provider}/${setting.settingKey}`}>
                <td className={'integration-settings-table-cell integration-settings-table-cell-name'}>
                  {setting.provider} / {setting.settingKey}
                </td>
                <td className={'integration-settings-table-cell'}>{setting.description}</td>
                <td className={'integration-settings-table-cell'}>
                  <IntegrationSettingSourceBadge source={setting.source} />
                </td>
                <td className={'integration-settings-table-cell'}>{setting.version}</td>
                <td className={'integration-settings-table-cell'}>
                  {setting.updatedAt ? (
                    <>
                      {formatDateTime(setting.updatedAt)}
                      <br />
                      {integrationSettingsStore.getOperatorName(setting.updatedBy)}
                    </>
                  ) : (
                    '-'
                  )}
                </td>
                <td className={'integration-settings-table-cell integration-settings-table-cell-actions'}>
                  <Button type={'outline'} size={'small'} onClick={() => openSetting(setting)}>
                    {i18n.getMessage('settings.integrations.button.edit')}
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
};

IntegrationSettingsTab.propTypes = {
  integrationSettingsStore: MobXPropTypes.observableObject
};

export default inject((stores) => ({
  integrationSettingsStore: stores.integrationSettingsStore
}))(observer(IntegrationSettingsTab));
